import {
    Accordion,
    AccordionContent,
    AccordionItem,
    AccordionTrigger,
} from '@/components/ui/accordion'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { FormField } from '@/components/ui/form-field'
import { Label } from '@/components/ui/label'
import { Separator } from '@/components/ui/separator'
import { QUERY_KEYS } from '@/constants/reactQueryKeys'
import { useMutationWithHandler } from '@/hooks/useMutationWithHandler'
import useUploadFile from '@/hooks/useUploadFile'
import DeleteOutlineIcons from '@/icons/DeleteOutlineIcons'
import { getProductsGroupOptions } from '@/modules/ProductGroup/services'
import {
    DNS_MANAGEMENT_TYPES,
    INTERNAL_SERVICE_STORAGE_PREFIX_URLS,
} from '@/modules/Service/constants'
import {
    createTechnicalTemplate,
    updateTechnicalTemplate,
} from '@/modules/Settings/services'
import {
    CreateTechnicalTemplatePayload,
    TechnicalTemplate,
} from '@/modules/Settings/types'
import { Attachment } from '@/modules/Upload/types'
import { ModalProps } from '@/types/shared'
import { omit, renderFileItem } from '@/utils/function'
import { FileText, Globe, Key, Paperclip, Plus, Settings } from 'lucide-react'
import React from 'react'
import { useFieldArray, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { Dialog, FormItem, GridHookForm, IconButton } from 'vnetwork-ui'

export interface CreateEditTechnicalTemplateModalProps extends ModalProps {
    template?: TechnicalTemplate | null
}

type FormData = CreateTechnicalTemplatePayload & { attachments?: File[] }

// Common metadata presets for quick-add
const METADATA_PRESETS = [
    { key: 'USER', value: '', label: 'User' },
    { key: 'PASS', value: '', label: 'Pass' },
    { key: 'LINK', value: '', label: 'Link' },
    { key: 'IP', value: '', label: 'IP' },
    { key: 'PORT', value: '', label: 'Port' },
    { key: 'DB_NAME', value: '', label: 'DB' },
] as const

const CreateEditTechnicalTemplateModal: React.FC<
    CreateEditTechnicalTemplateModalProps
> = ({ template, onClose, open }) => {
    const { t } = useTranslation()
    const isEdit = !!template

    const formControl = useForm<FormData>({
        defaultValues: template
            ? {
                  product_group_name: template.product_group_name,
                  template_name: template.template_name,
                  template_description: template.template_description,
                  dns_enable: template.dns_enable,
                  dns_managements: template.dns_managements || [],
                  metadata: template.metadata || [],
                  documentations: template.documentations || [],
                  attachments: [],
                  is_active: true,
              }
            : {
                  product_group_name: '',
                  template_name: '',
                  template_description: '',
                  dns_enable: false,
                  dns_managements: [],
                  metadata: [
                      { key: 'USER', value: '' },
                      { key: 'PASS', value: '' },
                      { key: 'LINK', value: '' },
                  ],
                  documentations: [{ title: '', url: '', description: '' }],
                  attachments: [],
                  is_active: true,
              },
    })

    const { control, watch, handleSubmit } = formControl

    const { uploadAsync, isUploading } = useUploadFile({
        prefix: INTERNAL_SERVICE_STORAGE_PREFIX_URLS.Notifications,
    })

    const {
        fields: dnsFields,
        append: appendDns,
        remove: removeDns,
    } = useFieldArray({ control, name: 'dns_managements' })

    const {
        fields: metadataFields,
        append: appendMetadata,
        remove: removeMetadata,
    } = useFieldArray({ control, name: 'metadata' })

    const {
        fields: docFields,
        append: appendDoc,
        remove: removeDoc,
    } = useFieldArray({ control, name: 'documentations' })

    const { mutateAsync, isPending } = useMutationWithHandler(
        {
            mutationFn: async (data: FormData) => {
                let uploadedAttachments: Attachment[] = []
                if (data.attachments && data.attachments.length > 0) {
                    const results = await uploadAsync(data.attachments)
                    uploadedAttachments = results
                        ?.filter((r) => r.status === 'fulfilled')
                        .map((r) => r.value) as Attachment[]
                }

                const payload = {
                    ...omit(data, ['attachments']),
                    attachments: uploadedAttachments,
                } as CreateTechnicalTemplatePayload

                if (isEdit && template) {
                    return updateTechnicalTemplate(
                        template.technical_template_id,
                        payload
                    )
                }
                return createTechnicalTemplate(payload)
            },
        },
        {
            successMessage: isEdit
                ? t('Template updated successfully')
                : t('Template created successfully'),
            reactQueryKeys: [QUERY_KEYS.TECHNICAL_TEMPLATES],
            onSuccess: () => onClose(),
        }
    )

    const isLoading = isPending || isUploading
    const dnsEnabled = watch('dns_enable')

    // Check which metadata presets are already added
    const existingMetaKeys = metadataFields.map((f) =>
        (f as any).key?.toUpperCase()
    )
    const availablePresets = METADATA_PRESETS.filter(
        (p) => !existingMetaKeys.includes(p.key)
    )

    return (
        <Dialog
            open={open}
            onClose={onClose}
            title={isEdit ? t('Edit Template') : t('Create New Template')}
            maxWidth="lg"
        >
            <form
                onSubmit={handleSubmit((data) => mutateAsync(data as any))}
                className="space-y-5"
            >
                {/* === SECTION 1: Basic Info === */}
                <div className="space-y-3">
                    <SectionHeader
                        icon={<Settings className="h-4 w-4" />}
                        title={t('basic-information')}
                    />
                    <div className="grid grid-cols-2 gap-3">
                        <div className="col-span-1">
                            <FormField
                                name="product_group_name"
                                type="select"
                                control={control}
                                label={t('product.groups')}
                                placeholder={t('product.groups')}
                                asyncOptions={getProductsGroupOptions}
                            />
                        </div>
                        <div className="col-span-1">
                            <FormItem
                                name="template_name"
                                type="textfield"
                                control={control}
                                label={t('template_name')}
                                placeholder="VD: Cấu hình dịch vụ Cloud Server"
                            />
                        </div>
                        <div className="col-span-2">
                            <FormItem
                                name="template_description"
                                type="textarea"
                                control={control}
                                label={t('template_description')}
                                placeholder={t('template_description')}
                            />
                        </div>
                        <div className="col-span-2">
                            <FormItem
                                name="dns_enable"
                                type="checkbox"
                                control={control}
                                label="Enable DNS Management"
                            />
                        </div>
                    </div>
                </div>

                {/* === SECTION 2-5: Accordion sections === */}
                <Accordion
                    type="multiple"
                    defaultValue={[
                        ...(dnsEnabled ? ['dns'] : []),
                        'metadata',
                        'documentation',
                    ]}
                    className="space-y-2"
                >
                    {/* DNS Management */}
                    {dnsEnabled && (
                        <AccordionItem
                            value="dns"
                            className="border rounded-lg px-4 bg-card"
                        >
                            <AccordionTrigger className="py-3 hover:no-underline">
                                <div className="flex items-center gap-2">
                                    <Globe className="h-4 w-4 text-blue-500" />
                                    <span className="font-medium text-sm">
                                        DNS Management
                                    </span>
                                    {dnsFields.length > 0 && (
                                        <Badge
                                            variant="outline-primary"
                                            className="h-5 px-1.5 text-xs"
                                        >
                                            {dnsFields.length}
                                        </Badge>
                                    )}
                                </div>
                            </AccordionTrigger>
                            <AccordionContent>
                                <div className="space-y-3">
                                    {dnsFields.map((field, index) => (
                                        <div
                                            key={field.id}
                                            className="grid grid-cols-12 gap-2 items-start"
                                        >
                                            <div className="col-span-4">
                                                <FormItem
                                                    name={`dns_managements.${index}.name`}
                                                    type="textfield"
                                                    control={control}
                                                    label="Name"
                                                    placeholder="@ / www / *"
                                                    size="small"
                                                />
                                            </div>
                                            <div className="col-span-2">
                                                <FormItem
                                                    name={`dns_managements.${index}.type`}
                                                    type="select"
                                                    control={control}
                                                    label="Type"
                                                    size="small"
                                                    allowClear={false}
                                                    options={
                                                        DNS_MANAGEMENT_TYPES
                                                    }
                                                />
                                            </div>
                                            <div className="col-span-5">
                                                <FormItem
                                                    name={`dns_managements.${index}.value`}
                                                    type="textfield"
                                                    control={control}
                                                    label="Value"
                                                    placeholder="IP / CNAME target"
                                                    size="small"
                                                />
                                            </div>
                                            <div className="col-span-1 flex items-end pb-1.5">
                                                <IconButton
                                                    size="small"
                                                    color="error"
                                                    onClick={() =>
                                                        removeDns(index)
                                                    }
                                                >
                                                    <DeleteOutlineIcons />
                                                </IconButton>
                                            </div>
                                        </div>
                                    ))}
                                    <Button
                                        type="button"
                                        size="sm"
                                        variant="outline"
                                        className="w-full border-dashed"
                                        onClick={() =>
                                            appendDns({
                                                type: 'A',
                                                name: '',
                                                value: '',
                                            })
                                        }
                                    >
                                        <Plus className="h-3.5 w-3.5 mr-1" />
                                        {t('add.dns_management')}
                                    </Button>
                                </div>
                            </AccordionContent>
                        </AccordionItem>
                    )}

                    {/* Metadata */}
                    <AccordionItem
                        value="metadata"
                        className="border rounded-lg px-4 bg-card"
                    >
                        <AccordionTrigger className="py-3 hover:no-underline">
                            <div className="flex items-center gap-2">
                                <Key className="h-4 w-4 text-amber-500" />
                                <span className="font-medium text-sm">
                                    Metadata (Credentials & Config)
                                </span>
                                {metadataFields.length > 0 && (
                                    <Badge
                                        variant="outline-primary"
                                        className="h-5 px-1.5 text-xs"
                                    >
                                        {metadataFields.length}
                                    </Badge>
                                )}
                            </div>
                        </AccordionTrigger>
                        <AccordionContent>
                            <div className="space-y-3">
                                {/* Quick-add preset buttons */}
                                {availablePresets.length > 0 && (
                                    <div className="flex flex-wrap gap-1.5">
                                        <span className="text-xs text-muted-foreground self-center mr-1">
                                            Quick add:
                                        </span>
                                        {availablePresets.map((preset) => (
                                            <Button
                                                key={preset.key}
                                                type="button"
                                                size="sm"
                                                variant="outline"
                                                className="h-6 px-2 text-xs"
                                                onClick={() =>
                                                    appendMetadata({
                                                        key: preset.key,
                                                        value: '',
                                                    })
                                                }
                                            >
                                                <Plus className="h-3 w-3 mr-0.5" />
                                                {preset.label}
                                            </Button>
                                        ))}
                                    </div>
                                )}

                                {metadataFields.map((field, index) => (
                                    <div
                                        key={field.id}
                                        className="grid grid-cols-12 gap-2 items-start"
                                    >
                                        <div className="col-span-4">
                                            <FormItem
                                                name={`metadata.${index}.key`}
                                                type="textfield"
                                                control={control}
                                                label="Key"
                                                placeholder="USER / PASS / LINK..."
                                                size="small"
                                            />
                                        </div>
                                        <div className="col-span-7">
                                            <FormItem
                                                name={`metadata.${index}.value`}
                                                type="textfield"
                                                control={control}
                                                label="Value"
                                                placeholder={t(
                                                    'service.metadata.value'
                                                )}
                                                size="small"
                                            />
                                        </div>
                                        <div className="col-span-1 flex items-end pb-1.5">
                                            <IconButton
                                                size="small"
                                                color="error"
                                                onClick={() =>
                                                    removeMetadata(index)
                                                }
                                            >
                                                <DeleteOutlineIcons />
                                            </IconButton>
                                        </div>
                                    </div>
                                ))}
                                <Button
                                    type="button"
                                    size="sm"
                                    variant="outline"
                                    className="w-full border-dashed"
                                    onClick={() =>
                                        appendMetadata({ key: '', value: '' })
                                    }
                                >
                                    <Plus className="h-3.5 w-3.5 mr-1" />
                                    {t('add.metadata.item')}
                                </Button>
                            </div>
                        </AccordionContent>
                    </AccordionItem>

                    {/* Documentation */}
                    <AccordionItem
                        value="documentation"
                        className="border rounded-lg px-4 bg-card"
                    >
                        <AccordionTrigger className="py-3 hover:no-underline">
                            <div className="flex items-center gap-2">
                                <FileText className="h-4 w-4 text-emerald-500" />
                                <span className="font-medium text-sm">
                                    Documentation
                                </span>
                                {docFields.length > 0 && (
                                    <Badge
                                        variant="outline-info"
                                        className="h-5 px-1.5 text-xs"
                                    >
                                        {docFields.length}
                                    </Badge>
                                )}
                            </div>
                        </AccordionTrigger>
                        <AccordionContent>
                            <div className="space-y-3">
                                {docFields.map((field, index) => (
                                    <div
                                        key={field.id}
                                        className="grid grid-cols-12 gap-2 items-start"
                                    >
                                        <div className="col-span-4">
                                            <FormItem
                                                name={`documentations.${index}.title`}
                                                type="textfield"
                                                control={control}
                                                label="Title"
                                                placeholder="Huong dan su dung..."
                                                size="small"
                                            />
                                        </div>
                                        <div className="col-span-7">
                                            <FormItem
                                                name={`documentations.${index}.url`}
                                                type="textfield"
                                                control={control}
                                                label="URL"
                                                placeholder="https://docs.example.com/..."
                                                size="small"
                                            />
                                        </div>
                                        <div className="col-span-1 flex items-end pb-1.5">
                                            <IconButton
                                                size="small"
                                                color="error"
                                                onClick={() => removeDoc(index)}
                                            >
                                                <DeleteOutlineIcons />
                                            </IconButton>
                                        </div>
                                    </div>
                                ))}
                                <Button
                                    type="button"
                                    size="sm"
                                    variant="outline"
                                    className="w-full border-dashed"
                                    onClick={() =>
                                        appendDoc({
                                            title: '',
                                            url: '',
                                            description: '',
                                        })
                                    }
                                >
                                    <Plus className="h-3.5 w-3.5 mr-1" />
                                    {t('add.documentation.item')}
                                </Button>
                            </div>
                        </AccordionContent>
                    </AccordionItem>

                    {/* Attachments */}
                    <AccordionItem
                        value="attachments"
                        className="border rounded-lg px-4 bg-card"
                    >
                        <AccordionTrigger className="py-3 hover:no-underline">
                            <div className="flex items-center gap-2">
                                <Paperclip className="h-4 w-4 text-purple-500" />
                                <span className="font-medium text-sm">
                                    {t('attachments')}
                                </span>
                                {(template?.attachments?.length || 0) > 0 && (
                                    <Badge
                                        variant="outline"
                                        className="h-5 px-1.5 text-xs"
                                    >
                                        {template?.attachments?.length}
                                    </Badge>
                                )}
                            </div>
                        </AccordionTrigger>
                        <AccordionContent>
                            <div className="space-y-3">
                                <GridHookForm
                                    formControl={formControl as any}
                                    subForm
                                    actions={[]}
                                    data={[
                                        {
                                            formItemProps: {
                                                name: 'attachments',
                                                type: 'files',
                                                renderFileItem: renderFileItem,
                                                multiple: true,
                                                label: t(
                                                    'Upload template documents'
                                                ),
                                                placeholder: t(
                                                    'Upload template documents'
                                                ),
                                            },
                                            className: 'col-span-4',
                                        },
                                    ]}
                                />
                                {template?.attachments &&
                                    template.attachments.length > 0 && (
                                        <div className="space-y-1.5 p-3 border rounded-md bg-muted/10">
                                            <Label className="text-xs text-muted-foreground">
                                                Existing Attachments:
                                            </Label>
                                            {template.attachments.map(
                                                (attachment, index) => (
                                                    <div
                                                        key={index}
                                                        className="flex items-center gap-2 text-xs"
                                                    >
                                                        <Paperclip className="h-3 w-3 text-muted-foreground" />
                                                        <a
                                                            href={
                                                                attachment.attachment_file_url
                                                            }
                                                            target="_blank"
                                                            rel="noopener noreferrer"
                                                            className="text-primary hover:underline"
                                                        >
                                                            {
                                                                attachment.attachment_file_name
                                                            }
                                                        </a>
                                                    </div>
                                                )
                                            )}
                                        </div>
                                    )}
                            </div>
                        </AccordionContent>
                    </AccordionItem>
                </Accordion>

                {/* === ACTIONS === */}
                <Separator />
                <div className="flex items-center justify-end gap-3">
                    <Button
                        type="button"
                        variant="outline"
                        onClick={onClose}
                        disabled={isLoading}
                    >
                        {t('cancel')}
                    </Button>
                    <Button type="submit" disabled={isLoading}>
                        {isLoading ? (
                            <span className="flex items-center gap-2">
                                <span className="h-4 w-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                                {isUploading ? 'Uploading...' : 'Saving...'}
                            </span>
                        ) : isEdit ? (
                            t('Update Template')
                        ) : (
                            t('Create Template')
                        )}
                    </Button>
                </div>
            </form>
        </Dialog>
    )
}

// Section header sub-component
function SectionHeader({
    icon,
    title,
}: {
    icon: React.ReactNode
    title: string
}) {
    return (
        <div className="flex items-center gap-2 text-primary">
            {icon}
            <Label className="text-sm font-semibold">{title}</Label>
        </div>
    )
}

export default CreateEditTechnicalTemplateModal
